import React, { useState, useEffect } from 'react';
import axiosInstance from "@/api/axios";
import { ArrowDown, ArrowUp } from 'lucide-react';
import { ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { useTranslation } from "react-i18next";

function getColorScheme(score) {
  if (score >= 70) return {
    primary: '#10b981',
    light: '#d1fae5',
    dark: '#059669',
    text: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-900/30',
    changeBg: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
  };
  if (score >= 50) return {
    primary: '#3b82f6',
    light: '#dbeafe',
    dark: '#2563eb',
    text: 'text-blue-500 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    changeBg: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
  };
  return {
    primary: '#ef4444',
    light: '#fee2e2',
    dark: '#dc2626',
    text: 'text-red-500 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-900/30',
    changeBg: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
  };
}

export default function RecyclabilityScore({
  year,
  bins,
  period
}) {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [changeRate, setChangeRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scoreData, setScoreData] = useState({
    recyclability_score: 0,
    change_rate: 0,
    details: {
      total_recyclable: 0,
      total_non_recyclable: 0,
      total_waste: 0
    }
  });
  const { t } = useTranslation();

  useEffect(() => {
      console.log('Fetching with:', { year, bins, period });

    let isMounted = true;
    const fetchRecyclabilityScore = async () => {
      setLoading(true);
      try {
        const params = {
          year: year,
          'bins[]': bins,
          period: period
        };

        const response = await axiosInstance.get('recyclability_score', { params });

        if (isMounted) {
          setScoreData(response.data);
          setChangeRate(response.data.change_rate || 0);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching recyclability score:', err);
        if (isMounted) {
          setError(t('recyclabilityScore.error'));
          setLoading(false);
        }
      }
    };

    fetchRecyclabilityScore();

    return () => {
      isMounted = false;
    };
  }, [bins, year, period, t]);

  const score = scoreData.recyclability_score;

  useEffect(() => {
      console.log('Received score data:', scoreData);

    if (animatedScore === score) return;

    const timeout = setTimeout(() => {
      setAnimatedScore(prev =>
        prev < score ? Math.min(prev + 1, score) : Math.max(prev - 1, score)
      );
    }, 20);

    return () => clearTimeout(timeout);
  }, [animatedScore, score]);

  const colorScheme = getColorScheme(score);
  const isNegative = changeRate < 0;

  const chartData = [
    {
      name: 'Max',
      value: 100,
      fill: '#f3f4f6'
    },
    {
      name: 'Score',
      value: animatedScore,
      fill: colorScheme.primary
    }
  ];

  const getPerformanceText = (score) => {
    if (score >= 70) return t('recyclabilityScore.performance.excellent');
    if (score >= 50) return t('recyclabilityScore.performance.meeting');
    return t('recyclabilityScore.performance.needsImprovement');
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 flex items-center justify-center w-full h-[200px]">
        <p className="text-sm text-gray-500">{t('recyclabilityScore.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 flex items-center justify-center w-full h-[200px]">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium">{t('recyclabilityScore.title')}</h2>
      </div>

      <div className="relative flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="70%"
            outerRadius="90%"
            data={chartData}
            startAngle={180}
            endAngle={0}
            barSize={10}
          >
            <RadialBar
              minAngle={15}
              background
              clockWise
              dataKey="value"
              cornerRadius={10}
            />
          </RadialBarChart>
        </ResponsiveContainer>

        <div
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-bold ${colorScheme.text}`}
        >
          {animatedScore.toFixed(1)}%
        </div>
      </div>

      <div className="px-4 pb-3 flex flex-col">
        <div className={`text-xs font-medium ${colorScheme.text} text-center`}>
          {getPerformanceText(score)}
        </div>
      </div>
    </div>
  );
}